import { useMemo, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mantine/core';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { AnalyticsSurface } from './AnalyticsSurface';
import type { TopicDefinition } from './TopicCatalogTypes';
import SurfaceBreadcrumbRow from './SurfaceBreadcrumbRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from './PageLevelFilterRow';
import { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';
import { sliceToPath } from './TopicSliceKey';
import PersonActivityGrid from './PersonActivityGrid';

interface SurfaceDrillBreakdownBodyProps {
    surface: AnalyticsSurface;
    definition: TopicDefinition;
    /** The decoded primary slice value being broken down (e.g. the clicked task category). */
    primaryKey: string;
}

/**
 * The intermediate drill page between a topic slice and its prompts: who is
 * behind this slice, ranked by activity. Clicking a row opens that person's
 * evidence (`…/:primaryKey/:userId`).
 */
export default function SurfaceDrillBreakdownBody({ surface, definition, primaryKey }: SurfaceDrillBreakdownBodyProps): ReactElement {
    const ambientFilters = useAssistFilters();
    const navigate = useNavigate();

    const filters = useMemo<AnalyticsFilter[]>(
        () => [{ field: definition.primaryField, values: [primaryKey] }, ...ambientFilters],
        [definition.primaryField, primaryKey, ambientFilters],
    );

    const drillLabel = definition.drillLabel ?? 'person';

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[
                    ...(surface.drillStyle === 'side-rail' ? [] : [{ label: surface.title, to: surface.basePath }]),
                    { label: definition.navLabel ?? definition.title, to: `${surface.basePath}/${definition.id}` },
                    { label: primaryKey },
                ]}
            />

            <PageHeaderComponent
                icon={definition.icon}
                eyebrow={`${definition.lensLabel} · by ${drillLabel}`}
                title={primaryKey}
                subtitle={`Who is behind this ${definition.lensLabel} — every ${drillLabel} ranked by activity. Open one for their prompts.`}
            />

            <PersonActivityGrid
                filters={filters}
                onRowClick={(userId) => navigate(sliceToPath(surface.basePath, definition.id, { primary: primaryKey, secondary: userId }))}
                persistenceKey={`drill-breakdown:${surface.id}:${definition.id}`}
            />
        </Box>
    );
}
